import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ExternalLink } from "lucide-react";
import { LEGAL_LINKS, LEGAL_DEFAULTS } from "@/pages/Legal";

export default function LegalAdmin() {
  const [form, setForm] = useState(null);
  const [active, setActive] = useState(LEGAL_LINKS[0].slug);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/legal").then((r) => {
      const ov = r.data || {};
      const merged = {};
      LEGAL_LINKS.forEach(({ slug }) => {
        const base = LEGAL_DEFAULTS[slug];
        const o = ov[slug] || {};
        merged[slug] = {
          title: base.title,
          intro: o.intro ?? base.intro,
          sections: (o.sections?.length ? o.sections : base.sections).map((s) => ({ ...s })),
        };
      });
      setForm(merged);
    });
  }, []);

  const setIntro = (slug, v) => setForm((f) => ({ ...f, [slug]: { ...f[slug], intro: v } }));
  const setSec = (slug, i, k, v) => setForm((f) => {
    const sections = [...f[slug].sections];
    sections[i] = { ...sections[i], [k]: v };
    return { ...f, [slug]: { ...f[slug], sections } };
  });
  const addSec = (slug) => setForm((f) => ({ ...f, [slug]: { ...f[slug], sections: [...f[slug].sections, { h: "", p: "" }] } }));
  const removeSec = (slug, i) => setForm((f) => ({ ...f, [slug]: { ...f[slug], sections: f[slug].sections.filter((_, x) => x !== i) } }));
  const resetPage = (slug) => setForm((f) => ({ ...f, [slug]: { title: LEGAL_DEFAULTS[slug].title, intro: LEGAL_DEFAULTS[slug].intro, sections: LEGAL_DEFAULTS[slug].sections.map((s) => ({ ...s })) } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/legal", form); toast.success("Legal pages updated"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-zinc-400" /></div>;

  const page = form[active];

  return (
    <div className="animate-fade-up max-w-3xl pb-16" data-testid="legal-admin-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Content</span>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-1">Legal pages</h1>
        </div>
        <div className="flex items-center gap-3">
          <a href={`/legal/${active}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm font-semibold border border-zinc-200 rounded-full px-4 py-2 hover:bg-zinc-50" data-testid="legal-preview-link">
            <ExternalLink size={14} /> Preview
          </a>
          <button onClick={save} disabled={saving} data-testid="legal-save" className="casper-btn rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />} Save changes
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {LEGAL_LINKS.map((l) => (
          <button key={l.slug} onClick={() => setActive(l.slug)} data-testid={`legal-tab-${l.slug}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active === l.slug ? "bg-black text-white" : "bg-white border border-zinc-200 text-zinc-500 hover:bg-zinc-50"}`}>
            {l.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200/80 rounded-xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-medium text-lg tracking-tight">{page.title}</h2>
          <button onClick={() => resetPage(active)} className="text-xs text-zinc-400 hover:text-rose-500" data-testid={`legal-reset-${active}`}>Reset to default</button>
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Intro</label>
          <textarea data-testid="legal-intro" rows={3} value={page.intro} onChange={(e) => setIntro(active, e.target.value)} className="lgin" />
        </div>
        <div>
          <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-2">Sections</label>
          <div className="space-y-3">
            {page.sections.map((s, i) => (
              <div key={i} className="border border-zinc-200 rounded-xl p-3 space-y-2" data-testid={`legal-section-${active}-${i}`}>
                <div className="flex gap-2">
                  <input value={s.h} onChange={(e) => setSec(active, i, "h", e.target.value)} placeholder="Heading" className="lgin font-semibold" data-testid={`legal-sec-h-${i}`} />
                  <button onClick={() => removeSec(active, i)} className="text-zinc-400 hover:text-rose-500 px-2" data-testid={`legal-sec-remove-${i}`}><Trash2 size={16} /></button>
                </div>
                <textarea value={s.p} onChange={(e) => setSec(active, i, "p", e.target.value)} rows={3} placeholder="Body text" className="lgin" data-testid={`legal-sec-p-${i}`} />
              </div>
            ))}
            <button onClick={() => addSec(active)} data-testid="legal-sec-add" className="flex items-center gap-2 text-sm font-semibold border border-zinc-200 rounded-full px-4 py-2 hover:bg-zinc-50">
              <Plus size={14} /> Add section
            </button>
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="legal-save-bottom" className="casper-btn rounded-full px-6 py-3 mt-6 text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
        {saving && <Loader2 size={14} className="animate-spin" />} Save changes
      </button>

      <style>{`.lgin{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:'Plus Jakarta Sans'}.lgin:focus{border-color:#4F46E5}`}</style>
    </div>
  );
}
